import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";

/*
  Controller name: sales-chart
  Targets:
    - from, to, btn, loading
    - dailyCanvas, weeklyCanvas, monthlyCanvas
*/

export default class extends Controller {
  static targets = ["from", "to", "btn", "loading", "dailyCanvas", "weeklyCanvas", "monthlyCanvas"]

  connect() {
    // initialize default dates (1 week)
    const today = new Date();
    const toVal = this.toTarget.value || this._formatDate(today);
    const fromVal = this.fromTarget.value || this._formatDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6));

    this.fromTarget.value = fromVal;
    this.toTarget.value = toVal;

    this.dailyChart = null;
    this.weeklyChart = null;
    this.monthlyChart = null;

    // initial load
    this.loadAndRender(fromVal, toVal);
  }

  async filter(e) {
    e.preventDefault();
    const from = this.fromTarget.value;
    const to = this.toTarget.value;
    if (!from || !to) { alert("Please select both from and to"); return; }
    await this.loadAndRender(from, to);
  }

  async loadAndRender(from, to) {
    try {
      this._showLoading(true);
      const data = await this._fetchSummary(from, to);
      // console.log("RAW JSON:", data) // Uncomment for debugging

      if (!data || !data.summary) throw new Error("No summary data");

      this._renderDaily(data.summary.daily || []);
      this._renderWeekly(data.summary.weekly || []);
      this._renderMonthly(data.summary.monthly || []);
    } catch (err) {
      console.error("sales-chart error:", err);
      // alert("Error fetching sales data: " + (err.message || err));
    } finally {
      this._showLoading(false);
    }
  }

  async _fetchSummary(from, to) {
    const baseUrl = this.data.get("summaryUrl") || "/sales/summary";
    const params = new URLSearchParams({ from, to });
    const url = `${baseUrl}?${params.toString()}`;

    const headers = {
      "Accept": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    };

    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    if (tokenMeta) headers["X-CSRF-Token"] = tokenMeta.getAttribute("content");

    const resp = await fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers
    });

    if (!resp.ok) {
      const txt = await resp.text();
      let parsed;
      try { parsed = JSON.parse(txt); } catch (e) { parsed = { error: txt || resp.statusText } }
      throw new Error(parsed.error || resp.statusText || "Fetch error");
    }
    return resp.json();
  }

  _renderDaily(data) {
    const { labels, totals } = this._prepareSeries(data);
    this._renderChart("dailyChart", this.dailyCanvasTarget, "Daily Sales", labels, totals);
  }

  _renderWeekly(data) {
    const { labels, totals } = this._prepareSeries(data);
    this._renderChart("weeklyChart", this.weeklyCanvasTarget, "Weekly Sales", labels, totals);
  }

  _renderMonthly(data) {
    const { labels, totals } = this._prepareSeries(data);
    this._renderChart("monthlyChart", this.monthlyCanvasTarget, "Monthly Sales", labels, totals);
  }

  _prepareSeries(arr) {
    const labels = arr.map(x => x.period);
    const totals = arr.map(x => Number(x.total_sales || 0));
    return { labels, totals };
  }

  _renderChart(key, canvas, title, labels, data) {
    // destroy previous if exists
    if (this[key]) { this[key].destroy(); this[key] = null; }

    // handle too many labels
    let displayLabels = labels;
    let displayData = data;
    if (labels.length > 50) {
      const step = Math.ceil(labels.length / 50);
      displayLabels = labels.filter((_, i) => i % step === 0);
      displayData = data.filter((_, i) => i % step === 0);
    }

    this[key] = new Chart(canvas.getContext("2d"), {
      type: "line",
      data: {
        labels: displayLabels,
        datasets: [{
          label: "Total Sales",
          data: displayData,
          borderColor: "#16a34a",       // Garis Hijau (Green-600)
          backgroundColor: "rgba(22, 163, 74, 0.1)", // Isi area bawah transparan hijau
          pointBackgroundColor: "#16a34a", // Titik hijau
          pointBorderColor: "#fff",     // Border titik putih
          pointHoverBackgroundColor: "#15803d", // Hover titik lebih gelap
          borderWidth: 2,               // Ketebalan garis
          tension: 0.3,                 // Kelengkungan garis (0 = lurus kaku)
          fill: true                    // Isi area di bawah garis
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }, // Sembunyikan legend jika hanya 1 dataset agar bersih
          title: { display: true, text: title, font: { size: 16 } },
          tooltip: {
            mode: 'index',
            intersect: false,
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { 
              callback: v => "Rp " + v.toLocaleString('id-ID') // Format Rupiah di Axis Y
            },
            grid: {
              color: "#f3f4f6" // Grid tipis abu-abu
            }
          },
          x: {
            grid: {
              display: false // Hilangkan grid vertikal agar bersih
            }
          }
        }
      }
    });
  }

  _showLoading(flag) {
    if (!this.hasLoadingTarget) return;
    this.loadingTarget.style.display = flag ? "inline-block" : "none";
  }

  _formatDate(d) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
}